import PropTypes from 'prop-types';
import { ENV } from '@/configs/env';
import { useSignin } from '@hooks/useSignin';
import { Form, Input, Button, Checkbox, Tooltip, Modal, Typography, message } from 'antd';
import { UserOutlined, LockOutlined, SafetyOutlined, DownloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getStudentPortalDownloadConfig } from '@services/registry';
import {
    AuthCenteredStack,
    AuthFullLogo,
    AuthPanel,
    AuthPanelHeader,
    AuthPanelTitle,
    AuthPanelSubtitle,
    AuthHelperText,
} from '../style';

const titles = {
    owner: 'Owner console',
    tenant: 'School portal',
};

const toYoutubeEmbedUrl = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    try {
        const url = new URL(raw);
        if (url.hostname.includes('youtu.be')) {
            const id = url.pathname.replace('/', '');
            return id ? `https://www.youtube.com/embed/${id}` : '';
        }
        if (url.hostname.includes('youtube.com')) {
            if (url.pathname.startsWith('/embed/')) return raw;
            const id = url.searchParams.get('v');
            return id ? `https://www.youtube.com/embed/${id}` : '';
        }
    } catch {
        return '';
    }
    return '';
};

const Signin = ({ portal = 'tenant' }) => {
    const { form, loading, onFinish } = useSignin(portal);
    const navigate = useNavigate();
    const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
    const [isPortalModalOpen, setIsPortalModalOpen] = useState(false);
    const [activeVideoUrl, setActiveVideoUrl] = useState('');
    const [adminCode, setAdminCode] = useState(['', '', '', '']);
    const [adminCodeError, setAdminCodeError] = useState('');
    const adminCodeInputsRef = useRef([]);
    const [portalConfig, setPortalConfig] = useState({
        apkUrl: '',
        apkVersion: '',
        installVideoUrl: '',
        teacherApkUrl: '',
        teacherApkVersion: '',
        teacherInstallVideoUrl: '',
    });

    const expectedAdminCode = useMemo(() => '8043', []);
    const enteredAdminCode = useMemo(() => adminCode.join(''), [adminCode]);

    useEffect(() => {
        let active = true;
        const loadPortalConfig = async () => {
            try {
                const config = await getStudentPortalDownloadConfig();
                if (!active) return;
                setPortalConfig({
                    apkUrl: config.apkUrl || '',
                    apkVersion: config.apkVersion || '',
                    installVideoUrl: config.installVideoUrl || '',
                    teacherApkUrl: config.teacherApkUrl || '',
                    teacherApkVersion: config.teacherApkVersion || '',
                    teacherInstallVideoUrl: config.teacherInstallVideoUrl || '',
                });
            } catch (error) {
                console.error(error);
            }
        };
        void loadPortalConfig();
        return () => {
            active = false;
        };
    }, []);

    const openAdminModal = () => {
        setAdminCode(['', '', '', '']);
        setAdminCodeError('');
        setIsAdminModalOpen(true);

        // Let modal mount first
        setTimeout(() => {
            adminCodeInputsRef.current?.[0]?.focus?.();
        }, 50);
    };

    const closeAdminModal = () => {
        setIsAdminModalOpen(false);
        setAdminCodeError('');
    };

    const submitAdminCode = () => {
        if (enteredAdminCode !== expectedAdminCode) {
            setAdminCodeError('Incorrect security code.');
            message.error('Incorrect security code.');
            adminCodeInputsRef.current?.[0]?.focus?.();
            return;
        }

        closeAdminModal();
        navigate('/owner/sign-in');
    };

    const setDigit = (idx, value) => {
        const cleaned = String(value ?? '').replace(/\D/g, '').slice(0, 1);
        setAdminCodeError('');
        setAdminCode((prev) => {
            const next = [...prev];
            next[idx] = cleaned;
            return next;
        });

        if (cleaned && idx < 3) {
            adminCodeInputsRef.current?.[idx + 1]?.focus?.();
        }
    };

    const onDigitKeyDown = (idx, e) => {
        if (e.key === 'Backspace' && !adminCode[idx] && idx > 0) {
            adminCodeInputsRef.current?.[idx - 1]?.focus?.();
            return;
        }

        if (e.key === 'ArrowLeft' && idx > 0) {
            adminCodeInputsRef.current?.[idx - 1]?.focus?.();
            return;
        }

        if (e.key === 'ArrowRight' && idx < 3) {
            adminCodeInputsRef.current?.[idx + 1]?.focus?.();
        }
    };

    return (
        <AuthCenteredStack style={{ padding: '0 16px', width: '100%', maxWidth: 520 }}>
            <AuthFullLogo src="/edulink-full.png" alt={`${ENV.APP_NAME} logo`} />
            <AuthPanel>
                <AuthPanelHeader>
                    <AuthPanelTitle>Welcome</AuthPanelTitle>
                    <AuthPanelSubtitle>{titles[portal] || 'Sign in'}</AuthPanelSubtitle>
                </AuthPanelHeader>
                <Form form={form} onFinish={onFinish} layout="vertical">
                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[{ required: true, message: 'Please input your email!' }]}
                    >
                        <Input
                            size="large"
                            prefix={<UserOutlined />}
                            placeholder="E.G. yourname@example.com"
                        />
                    </Form.Item>
                    <Form.Item
                        name="password"
                        label="Password"
                        rules={[{ required: true, message: 'Please input your password!' }]}
                    >
                        <Input.Password
                            size="large"
                            prefix={<LockOutlined />}
                            placeholder="Enter your password"
                        />
                    </Form.Item>
                    <Form.Item name="remember" valuePropName="checked">
                        <Checkbox>Remember me</Checkbox>
                    </Form.Item>
                    <Form.Item>
                        <Button size="large" type="primary" htmlType="submit" block loading={loading}>
                            Login
                        </Button>
                    </Form.Item>
                </Form>
                {portal === 'owner' ? (
                    <AuthHelperText>
                        <Link to="/tenant/sign-in">Login as Tenant</Link>
                    </AuthHelperText>
                ) : (
                    <>
                        <div style={{ marginTop: 16 }}>
                            <Button
                                type="primary"
                                size="large"
                                icon={<DownloadOutlined />}
                                onClick={() => setIsPortalModalOpen(true)}
                                style={{
                                    width: '100%',
                                    fontWeight: 600,
                                    height: 48,
                                    borderRadius: 12,
                                    background: 'linear-gradient(135deg, #1677ff 0%, #0958d9 100%)',
                                    border: 'none',
                                    boxShadow: '0 4px 12px rgba(22, 119, 255, 0.2)',
                                }}
                            >
                                Download Portal
                            </Button>
                        </div>
                        <AuthHelperText>Forgot password? Please contact your administrator.</AuthHelperText>
                    </>
                )}
            </AuthPanel>

            {portal !== 'owner' && (
                <Tooltip title="Admin / Owner sign in" placement="left">
                    <Button
                        type="primary"
                        shape="circle"
                        aria-label="Admin / Owner sign in"
                        size="large"
                        icon={<SafetyOutlined style={{ fontSize: 22 }} />}
                        onClick={openAdminModal}
                        style={{
                            position: 'fixed',
                            right: 'max(12px, env(safe-area-inset-right))',
                            bottom: 'max(12px, env(safe-area-inset-bottom))',
                            zIndex: 2000,
                            width: 56,
                            height: 56,
                            boxShadow: '0 12px 28px rgba(15, 23, 42, 0.25)',
                        }}
                    />
                </Tooltip>
            )}

            <Modal
                title="Security Code"
                open={isAdminModalOpen}
                onCancel={closeAdminModal}
                onOk={submitAdminCode}
                okText="Continue"
                cancelText="Cancel"
                destroyOnClose
            >
                <Typography.Paragraph style={{ marginBottom: 12, color: 'rgba(15, 23, 42, 0.65)' }}>
                    Enter the 4-digit security code to open Owner Login.
                </Typography.Paragraph>

                <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 8 }}>
                    {adminCode.map((digit, idx) => (
                        <Input
                            key={idx}
                            value={digit}
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            size="large"
                            maxLength={1}
                            status={adminCodeError ? 'error' : undefined}
                            onChange={(e) => setDigit(idx, e.target.value)}
                            onKeyDown={(e) => onDigitKeyDown(idx, e)}
                            onPressEnter={submitAdminCode}
                            ref={(el) => {
                                adminCodeInputsRef.current[idx] = el;
                            }}
                            style={{
                                width: 54,
                                height: 54,
                                textAlign: 'center',
                                fontSize: 22,
                                fontWeight: 700,
                            }}
                        />
                    ))}
                </div>

                {!!adminCodeError && (
                    <Typography.Text type="danger" style={{ display: 'block', textAlign: 'center' }}>
                        {adminCodeError}
                    </Typography.Text>
                )}
            </Modal>

            <Modal
                title={
                    <div style={{ fontSize: 20, fontWeight: 800, color: '#1e293b', textAlign: 'center', width: '100%', paddingBottom: 8 }}>
                        Select Portal to Download
                    </div>
                }
                open={isPortalModalOpen}
                onCancel={() => setIsPortalModalOpen(false)}
                footer={null}
                width={650}
                centered
                destroyOnClose
                styles={{ body: { padding: '8px 0 16px 0' } }}
            >
                <div style={{
                    display: 'flex',
                    gap: 24,
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    marginTop: 16
                }}>
                    {/* Student Portal Card */}
                    <div style={{
                        flex: '1 1 260px',
                        maxWidth: 290,
                        background: '#ffffff',
                        border: '1px solid rgba(15, 23, 42, 0.08)',
                        borderRadius: 20,
                        padding: 24,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05)',
                        transition: 'all 0.3s ease',
                    }}>
                        <div style={{ marginBottom: 16 }}>
                            <svg width="100" height="100" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect width="120" height="120" rx="24" fill="#E0F2FE"/>
                                <path d="M60 35L25 50L60 65L95 50L60 35Z" fill="#0284C7"/>
                                <path d="M35 59.3V75C35 80 46.2 85 60 85C73.8 85 85 80 85 75V59.3L60 70L35 59.3Z" fill="#0369A1"/>
                                <path d="M85 52V72" stroke="#0284C7" stroke-width="3" stroke-linecap="round"/>
                                <circle cx="85" cy="73" r="4" fill="#0284C7"/>
                            </svg>
                        </div>
                        <div style={{ fontWeight: 800, fontSize: 18, color: '#0f172a', marginBottom: 4 }}>
                            Student Portal
                        </div>
                        <div style={{ fontSize: 12, color: 'rgba(15, 23, 42, 0.5)', fontWeight: 600, marginBottom: 20 }}>
                            {portalConfig.apkVersion ? `Version ${portalConfig.apkVersion}` : 'Latest Version'}
                        </div>
                        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10, marginTop: 'auto' }}>
                            <Button
                                type="primary"
                                icon={<DownloadOutlined />}
                                href={portalConfig.apkUrl || undefined}
                                target={portalConfig.apkUrl ? '_blank' : undefined}
                                rel={portalConfig.apkUrl ? 'noreferrer' : undefined}
                                disabled={!portalConfig.apkUrl}
                                style={{
                                    width: '100%',
                                    height: 40,
                                    borderRadius: 10,
                                    fontWeight: 600,
                                    background: '#0284c7',
                                    borderColor: '#0284c7',
                                    boxShadow: '0 4px 10px rgba(2, 132, 199, 0.25)',
                                }}
                            >
                                Download APK
                            </Button>
                            {portalConfig.installVideoUrl && (
                                <Button
                                    type="default"
                                    icon={<InfoCircleOutlined />}
                                    onClick={() => {
                                        setActiveVideoUrl(portalConfig.installVideoUrl);
                                    }}
                                    style={{
                                        width: '100%',
                                        height: 40,
                                        borderRadius: 10,
                                        fontWeight: 600,
                                        color: '#0284c7',
                                        borderColor: '#bae6fd',
                                        background: '#f0f9ff',
                                    }}
                                >
                                    Watch Guide
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Teacher Portal Card */}
                    <div style={{
                        flex: '1 1 260px',
                        maxWidth: 290,
                        background: '#ffffff',
                        border: '1px solid rgba(15, 23, 42, 0.08)',
                        borderRadius: 20,
                        padding: 24,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05)',
                        transition: 'all 0.3s ease',
                    }}>
                        <div style={{ marginBottom: 16 }}>
                            <svg width="100" height="100" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect width="120" height="120" rx="24" fill="#FEE2E2"/>
                                <rect x="30" y="38" width="60" height="40" rx="4" fill="#EF4444"/>
                                <rect x="25" y="78" width="70" height="6" rx="2" fill="#DC2626"/>
                                <path d="M48 84L43 96H77L72 84H48Z" fill="#991B1B"/>
                                <rect x="38" y="46" width="44" height="4" rx="1" fill="#FFFFFF" opacity="0.9"/>
                                <rect x="38" y="54" width="44" height="4" rx="1" fill="#FFFFFF" opacity="0.9"/>
                                <rect x="38" y="62" width="28" height="4" rx="1" fill="#FFFFFF" opacity="0.9"/>
                            </svg>
                        </div>
                        <div style={{ fontWeight: 800, fontSize: 18, color: '#0f172a', marginBottom: 4 }}>
                            Teacher Portal
                        </div>
                        <div style={{ fontSize: 12, color: 'rgba(15, 23, 42, 0.5)', fontWeight: 600, marginBottom: 20 }}>
                            {portalConfig.teacherApkVersion ? `Version ${portalConfig.teacherApkVersion}` : 'Latest Version'}
                        </div>
                        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10, marginTop: 'auto' }}>
                            <Button
                                type="primary"
                                icon={<DownloadOutlined />}
                                href={portalConfig.teacherApkUrl || undefined}
                                target={portalConfig.teacherApkUrl ? '_blank' : undefined}
                                rel={portalConfig.teacherApkUrl ? 'noreferrer' : undefined}
                                disabled={!portalConfig.teacherApkUrl}
                                style={{
                                    width: '100%',
                                    height: 40,
                                    borderRadius: 10,
                                    fontWeight: 600,
                                    background: '#ef4444',
                                    borderColor: '#ef4444',
                                    boxShadow: '0 4px 10px rgba(239, 68, 68, 0.25)',
                                }}
                            >
                                Download APK
                            </Button>
                            {portalConfig.teacherInstallVideoUrl && (
                                <Button
                                    type="default"
                                    icon={<InfoCircleOutlined />}
                                    onClick={() => {
                                        setActiveVideoUrl(portalConfig.teacherInstallVideoUrl);
                                    }}
                                    style={{
                                        width: '100%',
                                        height: 40,
                                        borderRadius: 10,
                                        fontWeight: 600,
                                        color: '#ef4444',
                                        borderColor: '#fca5a5',
                                        background: '#fef2f2',
                                    }}
                                >
                                    Watch Guide
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </Modal>

            <Modal
                title="Portal Installation Guide"
                open={!!activeVideoUrl}
                onCancel={() => setActiveVideoUrl('')}
                footer={null}
                width={900}
                destroyOnClose
            >
                {activeVideoUrl ? (
                    toYoutubeEmbedUrl(activeVideoUrl) ? (
                        <iframe
                            title="Installation guide video"
                            src={toYoutubeEmbedUrl(activeVideoUrl)}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            style={{ width: '100%', height: 460, border: 0, borderRadius: 12 }}
                        />
                    ) : (
                        <video
                            controls
                            style={{ width: '100%', borderRadius: 12, background: '#000' }}
                            src={activeVideoUrl}
                        />
                    )
                ) : (
                    <Typography.Text type="secondary">
                        Installation help video URL is not configured yet.
                    </Typography.Text>
                )}
            </Modal>
        </AuthCenteredStack>
    );
};

Signin.propTypes = {
    portal: PropTypes.oneOf(['owner', 'tenant']),
};

export default Signin;
